import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  resolveBundleCascadeRemovals,
  resolveBundleQuantityUpdates,
} from '@theme/bundle-cart-guard';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors
 * @property {HTMLTableRowElement[]} cartItemRows
 * @property {TextComponent} cartTotal
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  async #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Ported from export theme: child lines cannot be edited alone;
    // parent qty change syncs the whole _bundle_group.
    try {
      const qtyResult = await resolveBundleQuantityUpdates({ line, quantity });
      if (qtyResult.blocked) {
        this.#handleCartError(line, {
          errors: 'セット商品の数量はメイン商品側で変更してください',
        });
        return;
      }

      if (qtyResult.updates) {
        this.updateQuantity({
          line,
          quantity,
          action: 'change',
          bundleUpdates: qtyResult.updates,
        });
      } else {
        this.updateQuantity({ line, quantity, action: 'change' });
      }
    } catch (error) {
      console.error(error);
      this.updateQuantity({ line, quantity, action: 'change' });
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;
    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * @param {number} line
   */
  async onLineItemRemove(line) {
    /** @type {string[]} */
    let pairedKeys = [];
    /** @type {Record<string, number> | null} */
    let bundleUpdates = null;

    try {
      const cascade = await resolveBundleCascadeRemovals({ line, quantity: 0 });
      pairedKeys = cascade.pairedKeys || [];
      if (cascade.updates) bundleUpdates = cascade.updates;
    } catch (error) {
      console.error('Bundle cascade resolve error:', error);
    }

    // Block direct child remove outside cascade (export theme behavior);
    // cascade already expands to whole group when child is removed.
    const row = this.refs.cartItemRows[line - 1];
    if (row?.dataset.bundleRole === 'child' && row?.dataset.bundleGroup && !bundleUpdates) {
      // Still try cascade — if group missing props, fall through to normal remove
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      pairedKeys,
      bundleUpdates,
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    /** @type {HTMLTableRowElement[]} */
    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((r) => r.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    for (const key of pairedKeys) {
      const pairedRow = this.refs.cartItemRows.find((r) => r.dataset.key === key);
      if (pairedRow && !rowsToRemove.includes(pairedRow)) {
        rowsToRemove.push(pairedRow);
        rowsToRemove.push(
          ...this.refs.cartItemRows.filter((r) => r.dataset.parentKey === pairedRow.dataset.key)
        );
      }
    }

    // Also match by bundle group attribute
    const groupId = cartItemRowToRemove.dataset.bundleGroup;
    if (groupId) {
      for (const r of this.refs.cartItemRows) {
        if (r.dataset.bundleGroup === groupId && !rowsToRemove.includes(r)) {
          rowsToRemove.push(r);
        }
      }
    }

    rowsToRemove.forEach((r) => {
      const remove = () => r.remove();
      if (prefersReducedMotion()) return remove();
      r.style.setProperty('--row-height', `${r.clientHeight}px`);
      r.classList.add('removing');
      onAnimationEnd(r, remove);
    });
  }

  /**
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   * @param {string[]} [config.pairedKeys]
   * @param {Record<string, number>} [config.bundleUpdates]
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sections = Array.from(sectionsToUpdate).join(',');
    const sectionsUrl = window.location.pathname;

    /** @type {Record<string, number> | null} */
    let bundleUpdates = config.bundleUpdates || null;

    if (quantity === 0 && !bundleUpdates) {
      try {
        const cascade = await resolveBundleCascadeRemovals({ line, quantity: 0 });
        if (cascade.updates) bundleUpdates = cascade.updates;
      } catch (error) {
        console.error('Bundle cascade resolve error:', error);
      }
    }

    cartTotal?.shimmer();

    const cartUpdateUrl = `${Theme.routes.cart_update_url}${
      String(Theme.routes.cart_update_url).endsWith('.js') ? '' : '.js'
    }`;

    const request =
      bundleUpdates && Object.keys(bundleUpdates).length > 0
        ? fetch(
            cartUpdateUrl,
            fetchConfig('json', {
              body: JSON.stringify({
                updates: bundleUpdates,
                sections,
                sections_url: sectionsUrl,
              }),
            })
          )
        : fetch(
            `${Theme.routes.cart_change_url}`,
            fetchConfig('json', {
              body: JSON.stringify({
                line,
                quantity,
                sections,
                sections_url: sectionsUrl,
              }),
            })
          );

    request
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const sectionHtml = parsedResponseText.sections?.[this.sectionId];
        if (!sectionHtml) {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;
        input.setAttribute('data-cart-quantity', item.quantity.toString());
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
